/**
 * @file CalculateurChargesCrossfit.tsx
 * @summary Article de blog — CalculateurChargesCrossfit
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import SEO from "@/components/SEO";
import BlogHeroImage, { getArticleImageUrls, withArticleImages, slugFromUrl } from "@/components/blog/BlogHeroImage";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowLeft, Calculator, Dumbbell, BookOpen, Activity, Target } from "lucide-react";
import BlogCTA, { BlogStickyCTA } from "@/components/blog/BlogCTA";
import { Button } from "@/components/ui/button";

const URL = "https://crossfitchistera.fr/blog/calculateur-charges-crossfit-1rm";

const SLUG = slugFromUrl(URL);
const IMAGES = getArticleImageUrls(SLUG, 5);
const faq = [
  { "@type": "Question", name: "Qu'est-ce qu'un 1RM ?", acceptedAnswer: { "@type": "Answer", text: "Le 1RM (One Repetition Maximum) est la charge maximale qu'une personne peut soulever une seule fois avec une technique correcte sur un mouvement donné." } },
  { "@type": "Question", name: "Faut-il vraiment tester son 1RM réel ?", acceptedAnswer: { "@type": "Answer", text: "Pas nécessairement. Une estimation à partir d'une série sous-maximale (par exemple 5 répétitions à 80 kg) suffit dans la majorité des cas pour programmer intelligemment." } },
  { "@type": "Question", name: "Quelle formule de calcul choisir ?", acceptedAnswer: { "@type": "Answer", text: "Aucune n'est parfaite. Epley, Brzycki, Lombardi, Mayhew, Wathan, Lander et O'Conner donnent des estimations cohérentes, surtout entre 2 et 10 répétitions. Le calculateur Chistera permet de comparer." } },
  { "@type": "Question", name: "Sur quelle plage de répétitions les estimations sont-elles fiables ?", acceptedAnswer: { "@type": "Answer", text: "Les formules sont surtout fiables entre 2 et 10 répétitions. Au-delà, l'endurance, la fatigue et le cardio influencent davantage le résultat." } },
  { "@type": "Question", name: "Le calculateur remplace-t-il le coach ?", acceptedAnswer: { "@type": "Answer", text: "Non. Le calculateur est un outil. Si la technique se dégrade à 85 kg alors qu'il indique 100 kg, la bonne charge du jour reste 85 kg." } },
];

const jsonLd = [
  { "@context": "https://schema.org", "@type": "Article", headline: "Calculateur de charges CrossFit : 1RM, PR et méthodes", description: "Comprendre et utiliser le calculateur 1RM CrossFit Chistera : Epley, Brzycki, Lombardi, Mayhew, Wathan et pourcentages de travail.", author: { "@type": "Organization", name: "CrossFit Chistera" }, publisher: { "@type": "Organization", name: "CrossFit Chistera", logo: { "@type": "ImageObject", url: "https://crossfitchistera.fr/og-image-square.png?v=4" } }, image: "https://crossfitchistera.fr/og-image-square.png?v=4", datePublished: "2026-01-15", dateModified: "2026-05-15", mainEntityOfPage: URL, inLanguage: "fr-FR" },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq },
  {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "Estimer son 1RM avec le calculateur CrossFit Chistera",
    step: [
      { "@type": "HowToStep", name: "Choisir l'exercice", text: "Sélectionner le mouvement (squat, deadlift, bench…)." },
      { "@type": "HowToStep", name: "Entrer la charge", text: "Indiquer la charge utilisée en kilogrammes." },
      { "@type": "HowToStep", name: "Entrer le nombre de répétitions", text: "Indiquer le nombre de répétitions réalisées avec une technique correcte." },
      { "@type": "HowToStep", name: "Lancer le calcul", text: "Le calculateur estime le 1RM probable et affiche les pourcentages de travail." },
    ],
  },
  { "@context": "https://schema.org", "@type": "WebApplication", name: "Calculateur de charges CrossFit Chistera", url: "https://crossfitchistera.fr/charges", applicationCategory: "HealthApplication", operatingSystem: "Web" },
];

const formules = [
  { name: "Epley", formula: "1RM = charge × (1 + reps / 30)", ex: "≈ 93,3 kg", note: "Très populaire et simple. Peut surestimer légèrement quand le nombre de reps augmente." },
  { name: "Brzycki", formula: "1RM = charge × 36 / (37 − reps)", ex: "= 90 kg", note: "Estimation prudente, particulièrement utilisée entre 2 et 10 répétitions." },
  { name: "Lombardi", formula: "1RM = charge × reps^0,10", ex: "≈ 94 kg", note: "Modèle en fonction puissance, intéressant dans les comparateurs multi-formules." },
  { name: "Mayhew", formula: "1RM = (100 × charge) / (52,2 + 41,9 × e^(−0,055 × reps))", ex: "≈ 92-93 kg", note: "Modèle exponentiel, historiquement utilisé sur le bench press." },
  { name: "Wathan", formula: "1RM = (100 × charge) / (48,8 + 53,8 × e^(−0,075 × reps))", ex: "≈ 93 kg", note: "Modèle exponentiel souvent utilisé dans les calculateurs avancés." },
  { name: "Lander", formula: "1RM = charge / (1,013 − 0,0267123 × reps)", ex: "≈ 91 kg", note: "Formule historique, cohérente sur les faibles répétitions." },
  { name: "O'Conner", formula: "1RM = charge × (1 + 0,025 × reps)", ex: "= 90 kg", note: "Formule simple, estimation prudente." },
];

const sources = [
  { label: "Brzycki, M. — Strength Testing: Predicting a One-Rep Max from Reps-to-Fatigue (1993)", url: "https://www.paulogentil.com/pdf/Strength%20Testing%E2%80%94Predicting%20a%20One-Rep%20Max%20from%20Reps-to-Fatigue.pdf" },
  { label: "Ware et al., 1997 — Accuracy of prediction equations for estimating 1RM", url: "https://dyrts.fr/fr/files/1RM_estimation.pdf" },
  { label: "Rontu et al., 2024 — Predicting 1RM in novice males (ScienceDirect)", url: "https://www.sciencedirect.com/science/article/pii/S1360859224003619" },
  { label: "Epley Formula", url: "https://www.1rmcalculator.org/formulas/epley" },
  { label: "Lombardi Formula", url: "https://www.1rmcalculator.org/formulas/lombardi" },
  { label: "Comparaison des formules 1RM", url: "https://www.1rmcalculator.org/formulas/comparison" },
];

export default function CalculateurChargesCrossfit() {
  return (
    <>
      <SEO
        title="Calculateur de charges CrossFit : 1RM, PR et méthodes"
        description="Comprendre et utiliser le calculateur de charges CrossFit Chistera : 1RM, PR, Epley, Brzycki, Lombardi, Mayhew et pourcentages."
        canonical={URL}
        jsonLd={withArticleImages(jsonLd, IMAGES)}
          ogImages={IMAGES}
      />
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-32 md:pb-20">
          <article className="container mx-auto px-4 max-w-4xl">
            <BlogHeroImage slug={SLUG} />
            <nav aria-label="Fil d'Ariane" className="mb-6 text-sm text-muted-foreground flex items-center flex-wrap gap-1">
              <Link to="/" className="inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="w-3 h-3" /> Accueil</Link>
              <ChevronRight className="w-3 h-3" />
              <span>Blog</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-foreground">Calculateur 1RM</span>
            </nav>

            <header className="mb-10">
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-gold tracking-wider mb-3 font-body normal-case uppercase">
                <Calculator className="w-4 h-4" /> Outil & guide · Lecture ~ 9 min
              </span>
              <h1 className="text-4xl md:text-6xl text-foreground mb-6 font-heading">Calculateur de charges CrossFit : comprendre son 1RM et ses PR</h1>
              <p className="text-lg text-foreground/85 leading-relaxed mb-4">
                Quand on commence le CrossFit, on entend rapidement parler de PR, de 1RM, de pourcentages, de séries à 70 %, de cycles de force. Au début, soyons honnêtes : cela peut ressembler à un mélange de maths et de jargon de salle.
              </p>
              <p className="text-lg text-foreground/85 leading-relaxed mb-8">
                C'est justement pour aider les adhérents à mieux comprendre leurs charges que CrossFit Chistera propose un <Link to="/charges">calculateur pratique</Link> directement accessible sur le site : estimer son 1RM, suivre ses PR, calculer ses pourcentages de travail, mieux gérer ses séances et éviter de charger « au hasard ».
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="bg-cta text-cta-foreground hover:bg-cta/90 font-body normal-case">
                  <Link to="/charges"><Calculator className="w-4 h-4" /> Ouvrir le calculateur</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-gold text-gold hover:bg-gold hover:text-gold-foreground font-body normal-case">
                  <Link to="/lexique"><BookOpen className="w-4 h-4" /> Voir le lexique</Link>
                </Button>
              </div>
            </header>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground prose-li:text-foreground/85">

              <h2>Qu'est-ce qu'un 1RM ?</h2>
              <p>Le 1RM signifie <strong>One Repetition Maximum</strong> — en français, répétition maximale. C'est la charge maximale qu'une personne peut soulever une seule fois avec une technique correcte. Le 1RM est utilisé pour le squat, le deadlift, le bench press, le strict press, le front squat, le clean et certains mouvements d'haltérophilie. Ensuite, les coachs utilisent ce repère pour programmer les entraînements.</p>
              <p><strong>Exemple :</strong> si votre 1RM au back squat est estimé à 100 kg, alors 60 % = 60 kg, 70 % = 70 kg, 80 % = 80 kg, 90 % = 90 kg. Simple… mais très utile.</p>

              <h2>Pourquoi utiliser un calculateur plutôt que tester son max réel ?</h2>
              <p>Tester un vrai 1RM peut être intéressant. Mais ce n'est pas toujours nécessaire. Un test maximal demande une bonne technique, un bon échauffement, une récupération correcte, un environnement sécurisé et idéalement un coach.</p>
              <p>Les formules d'estimation permettent de partir d'une série sous-maximale. Exemple : vous réalisez 5 répétitions à 80 kg, le calculateur estime alors votre 1RM probable. Ce n'est pas une vérité absolue, c'est une <strong>estimation de travail</strong>. Et dans la majorité des cas, cela suffit largement pour programmer intelligemment.</p>

              <h2>Comment fonctionne le calculateur CrossFit Chistera ?</h2>
              <ol>
                <li>Choisir un exercice</li>
                <li>Entrer la charge utilisée</li>
                <li>Entrer le nombre de répétitions</li>
                <li>Lancer le calcul</li>
                <li>Obtenir une estimation du 1RM</li>
                <li>Consulter les pourcentages de travail (60 %, 70 %, 80 %, 90 %)</li>
              </ol>
              <p><strong>Exemple :</strong> 80 kg × 5 répétitions au squat. Selon les formules utilisées, le 1RM estimé sera autour de 90 à 95 kg. Le calculateur affiche ensuite les pourcentages, ce qui permet de mieux suivre les consignes du coach.</p>

              <h2>Les principales formules utilisées dans les calculateurs 1RM</h2>
              <p>Il existe plusieurs formules historiques reconnues pour estimer un 1RM. Aucune n'est parfaite — mais elles donnent des estimations utiles et cohérentes.</p>
            </div>

            <div className="not-prose my-8 grid sm:grid-cols-2 gap-4">
              {formules.map((f) => (
                <div key={f.name} className="border border-border bg-card rounded-lg p-5">
                  <p className="font-heading uppercase tracking-wider text-gold mb-2">Formule de {f.name}</p>
                  <p className="text-sm font-mono text-foreground/90 bg-charcoal/40 rounded px-2 py-1.5 mb-2 break-words">{f.formula}</p>
                  <p className="text-xs font-body normal-case text-foreground/70 mb-2">Exemple 80 kg × 5 reps : <span className="text-gold">{f.ex}</span></p>
                  <p className="text-xs font-body normal-case text-foreground/80 leading-relaxed">{f.note}</p>
                </div>
              ))}
            </div>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground prose-li:text-foreground/85">

              <h2>Tableau comparatif des estimations</h2>
              <p>Exemple : 80 kg × 5 répétitions.</p>
            </div>

            <div className="not-prose my-8 overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm font-body normal-case">
                <thead className="bg-charcoal/60 text-gold">
                  <tr>
                    <th className="text-left px-3 py-3 font-heading uppercase tracking-wider text-xs">Méthode</th>
                    <th className="text-right px-3 py-3 font-heading uppercase tracking-wider text-xs">1RM estimé</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {formules.map((f) => (
                    <tr key={f.name} className="hover:bg-charcoal/30">
                      <td className="px-3 py-3 text-foreground/90 font-medium">{f.name}</td>
                      <td className="px-3 py-3 text-gold text-right">{f.ex.replace(/^[≈=]\s?/, "")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground prose-li:text-foreground/85">

              <p>Cela montre une chose importante : <strong>le 1RM estimé est une zone de travail</strong>, pas une vérité absolue.</p>

              <h2>Pourquoi les estimations sont surtout fiables entre 2 et 10 répétitions</h2>
              <p>Au-delà de 10 répétitions, plusieurs paramètres influencent davantage le résultat : endurance musculaire, fatigue, cardio, technique, motivation, profil individuel. Les études montrent que la précision varie selon l'exercice, le sexe, l'expérience, la formule utilisée et le nombre de répétitions (Ware et al., 1997 ; Rontu et al., 2024).</p>

              <h2>Le calculateur ne remplace pas le coaching</h2>
              <p>Le calculateur est un outil. Mais il ne remplace pas la technique, le regard du coach, l'échauffement, la récupération et l'adaptation individuelle. Si le calculateur indique 100 kg mais que la technique devient mauvaise à 85 kg : la bonne charge du jour reste 85 kg.</p>
              <p className="text-xs italic text-muted-foreground">Avertissement : cet article et l'outil associé reprennent la littérature sportive et scientifique du moment. Ils ne remplacent pas un avis médical ou professionnel de santé. CrossFit Chistera n'est pas un professionnel de santé et n'est pas habilité comme tel.</p>
            </div>

            <div className="my-12">
              <BlogCTA />
            </div>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground prose-li:text-foreground/85">

              <h2>FAQ — Calculateur 1RM CrossFit</h2>
              {faq.map((q) => (
                <div key={q.name}>
                  <h3>{q.name}</h3>
                  <p>{q.acceptedAnswer.text}</p>
                </div>
              ))}

              <h2>Sources scientifiques et techniques</h2>
              <ul>
                {sources.map((s) => (
                  <li key={s.url}><a href={s.url} target="_blank" rel="noopener noreferrer">{s.label}</a></li>
                ))}
              </ul>
            </div>

            <aside className="border-l-4 border-gold bg-charcoal/40 px-6 py-5 mt-12 rounded-r-md">
              <p className="text-sm uppercase tracking-wider text-gold font-heading mb-3">À retenir</p>
              <p className="text-foreground/90 font-body normal-case leading-relaxed">
                Le 1RM estimé est une <strong>zone de travail</strong>, pas un dogme. Les formules d'Epley, Brzycki, Lombardi, Mayhew, Wathan, Lander ou O'Conner offrent toutes des estimations cohérentes entre 2 et 10 répétitions. Le calculateur Chistera regroupe ces méthodes pour vous aider à programmer intelligemment, mais il ne remplace ni la technique ni l'œil du coach.
              </p>
            </aside>

            <div className="mt-12">
              <BlogCTA variant="hero" />
            </div>

            <section className="mt-16 border-t border-border pt-10">
              <h2 className="text-2xl font-heading text-foreground mb-6">À lire aussi</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/charges" className="border border-border bg-card rounded-lg p-5 hover:border-gold transition-colors">
                  <Calculator className="w-5 h-5 text-gold mb-2" />
                  <p className="font-heading uppercase tracking-wider text-sm text-foreground">Calculateur de charges</p>
                </Link>
                <Link to="/lexique" className="border border-border bg-card rounded-lg p-5 hover:border-gold transition-colors">
                  <BookOpen className="w-5 h-5 text-gold mb-2" />
                  <p className="font-heading uppercase tracking-wider text-sm text-foreground">Lexique CrossFit</p>
                </Link>
                <Link to="/blog/comment-se-passe-une-seance-crossfit" className="border border-border bg-card rounded-lg p-5 hover:border-gold transition-colors">
                  <Activity className="w-5 h-5 text-gold mb-2" />
                  <p className="font-heading uppercase tracking-wider text-sm text-foreground">Une séance type</p>
                </Link>
                <Link to="/blog/guide-complet-crossfit" className="border border-border bg-card rounded-lg p-5 hover:border-gold transition-colors">
                  <Target className="w-5 h-5 text-gold mb-2" />
                  <p className="font-heading uppercase tracking-wider text-sm text-foreground">Guide complet CrossFit</p>
                </Link>
              </div>
            </section>
            <BlogCTA single primaryLabel="Réserver mon essai gratuit" />

          </article>
        </main>
        <BlogStickyCTA />
        <Footer />
      </div>
    </>
  );
}
